import {
  Box,
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { FilterCell, TableFilterInput } from '../user-dash/filterCell';
import { ExportButtonSx } from '../gamification-dash/dqShared';
import { formatDateTime, PAGE_SIZE_OPTIONS, ROLE_FILTER_OPTIONS, TRUE_FALSE_FILTER_OPTIONS } from './messagesUtils';

const HEADER_CELL_SX = { fontWeight: 'bold', color: '#003ABA' };

const COLUMNS = [
  { id: 'id', label: 'ID', placeholder: 'Search ID' },
  { id: 'conversationId', label: 'Conversation', placeholder: 'Search Conversation' },
  { id: 'userId', label: 'User ID', placeholder: 'Search User ID' },
  { id: 'email', label: 'Email', placeholder: 'Search Email' },
  { id: 'role', label: 'Role', filterType: 'select', filterOptions: ROLE_FILTER_OPTIONS },
  { id: 'isFollowUp', label: 'Follow-up', filterType: 'select', filterOptions: TRUE_FALSE_FILTER_OPTIONS },
  { id: 'createdAt', label: 'Created At' },
  { id: 'content', label: 'Content', placeholder: 'Search Content' },
];

const truncateContent = (value, max = 120) => {
  if (!value) return '-';
  const text = String(value);
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const MessagesTableSection = ({
  messages,
  total,
  periodLabel,
  tableLoaded,
  page,
  rowsPerPage,
  filters,
  tableLoading,
  onLoadMessages,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  onExportTable,
}) => (
  <Paper sx={{ mt: 3 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        mt: 1,
      }}
    >
      <Typography variant="h6" sx={{ fontSize: 20 }}>
        Chat Messages
        {periodLabel ? (
          <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            ({periodLabel})
          </Typography>
        ) : null}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={onExportTable}
          disabled={tableLoading || !tableLoaded}
          sx={ExportButtonSx}
          startIcon={
            <Box component="img" src="/icons/export-csv.svg" alt="export" sx={{ width: 40, height: 24 }} />
          }
        >
          Export chat messages
        </Button>
      </Box>
    </Box>

    {!tableLoaded ? (
      <Box
        sx={{
          minHeight: 320,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          pb: 4,
        }}
      >
        <Typography color="textSecondary" sx={{ textAlign: 'center' }}>
          Messages are not loaded yet. Use the button below to load messages for the selected period.
        </Typography>
        <Button variant="contained" onClick={onLoadMessages} disabled={tableLoading}>
          {tableLoading ? 'Loading…' : `Load messages for ${periodLabel}`}
        </Button>
      </Box>
    ) : (
      <>
        <TableContainer
          component={Paper}
          sx={{ minHeight: 400, maxHeight: 700, overflowX: 'auto', overflowY: 'auto', width: '100%' }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: 'auto',
              width: 'max-content',
              minWidth: '100%',
              '& .MuiTableCell-root': {
                borderRight: '1px solid #e0e0e0',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'clip',
              },
            }}
          >
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <FilterCell key={`filter-${col.id}`}>
                    {col.filterType === 'select' ? (
                      <TableFilterInput
                        select
                        value={filters[col.id] ?? ''}
                        onChange={(e) => onSearchChange(col.id, e.target.value)}
                        SelectProps={{ displayEmpty: true }}
                      >
                        {(col.filterOptions || []).map((opt) => (
                          <MenuItem key={opt.value || 'all'} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TableFilterInput>
                    ) : col.placeholder ? (
                      <TableFilterInput
                        value={filters[col.id] ?? ''}
                        onChange={(e) => onSearchChange(col.id, e.target.value)}
                        placeholder={col.placeholder}
                      />
                    ) : null}
                  </FilterCell>
                ))}
              </TableRow>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={`header-${col.id}`} sx={HEADER_CELL_SX}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                    {tableLoading ? 'Loading…' : 'No messages found.'}
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.conversationId}</TableCell>
                    <TableCell>{row.userId}</TableCell>
                    <TableCell>{row.email || '-'}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{row.role || '-'}</TableCell>
                    <TableCell>{row.isFollowUp ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: 'normal' }} title={row.content}>
                      {truncateContent(row.content)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        />
      </>
    )}
  </Paper>
);

export default MessagesTableSection;
